import appInfo from "@common/appInfo";
import { type GenericMap } from "@common/declarations";
import * as format from "@common/lib/format";
import { type I18n } from "@common/lib/i18n";
import { joinDefined } from "@common/lib/misc";
import { isFunction } from "lodash";
import { type GridColumn, type GridGroupInfo, type WebsiteValue } from "./types";

export const NO_VALUE = "-";

const getDataTypesToLabels = (
  i18n: I18n
): GenericMap<{
  false: string;
  true: string;
}> => ({
  active: {
    false: i18n.inactive,
    true: i18n.active
  },
  boolean: {
    false: i18n.no,
    true: i18n.yes
  },
  enabled: {
    false: i18n.disabled,
    true: i18n.enabled
  },
  published: {
    false: i18n.unpublished,
    true: i18n.published
  },
  archived: {
    false: i18n.active,
    true: i18n.archived
  }
});

export const formatMoneyDisplay = format.formatMoneyDisplay;
export const formatPercentDisplay = format.formatPercentDisplay;
export const formatCellDisplayValue = format.formatDisplayValue;

export const getWebsiteValue = <T extends {}>(item: T): WebsiteValue | null => {
  if (item && typeof item === "object" && "domain" in item) {
    return {
      domain: item["domain"] as any,
      subdomain: item["portalSubdomain"],
      name: item["portalName"]
    };
  }

  return null;
};

export const getFormattedValue = <T extends {}>(i18n: I18n, col: GridColumn<T>, item: T): any => {
  switch (col.dataType) {
    case "active":
    case "archived":
    case "published":
    case "boolean":
    case "enabled":
      const labels = getDataTypesToLabels(i18n)[col.dataType];
      return item[col.key] ? labels.true : labels.false;
    case "website": {
      const value = getWebsiteValue(item);
      if (value) {
        return value.domain ? joinDefined(" ", value.subdomain, value.domain) : value.name;
      }
      return value;
    }
    default: {
      return formatCellDisplayValue(col.key && item[col.key], col.dataType);
    }
  }
};

export const isSearchMatch = <T extends {}>(
  i18n: I18n,
  col: GridColumn<T>,
  item: T,
  searchText: string | undefined
): boolean => {
  if (!searchText) return true;

  if (!isColumnFilterable(col)) return false;

  const value = getFormattedValue(i18n, col, item);

  if (!value || typeof value === "object") return false;

  return value.toString().toLowerCase().includes(searchText.toLowerCase());
};

export const isColumnFilterable = <T extends {}>(column: GridColumn<T>): boolean => column.dataType !== "custom";
export const isColumnSortable = <T extends {}>(column: GridColumn<T>): boolean =>
  // temporary is not supported
  // column.isBESortable || column.dataType !== "custom";
  false;
export const isColumnVisible = <T extends {}>(column: GridColumn<T>): boolean => {
  if (!column.visibility) return true;
  if (isFunction(column.visibility)) return column.visibility();
  return appInfo.system.brandKey === column.visibility;
};

// TODO: this is a HUGE hack!
export const isGroupItem = (item: any): item is GridGroupInfo =>
  Object.keys(item).length == 3 && "id" in item && "label" in item && "value" in item;

// NOTE: couldn't come up with a better solution. Iterate over cells, detect fully empty columns and hide them.
export const hideEmptyColumns = (table: HTMLTableElement) => {
  const hasData: boolean[] = [];

  const rows = table.querySelectorAll("tbody tr")!;
  rows.forEach((r) => {
    if (r.classList.contains("group")) return;

    const cells = r.querySelectorAll("td");
    cells.forEach((c, j) => {
      if (!hasData[j]) {
        hasData[j] = !!c.innerHTML && c.innerText !== NO_VALUE;
      }
    });
  });

  const visible = hasData.filter((x) => x).length;
  hasData.forEach((x, j) => {
    const th = table.querySelector(`thead > tr > th:nth-child(${j + 1})`) as HTMLElement;
    th.style.display = x ? "" : "none";

    rows.forEach((r) => {
      if (r.classList.contains("group")) {
        const firstCell = r.querySelector(`td:nth-child(${1})`) as HTMLTableCellElement;
        const actionsCell = r.querySelector(`td.actions`) as HTMLTableCellElement;
        firstCell.colSpan = visible - (actionsCell ? 1 : 0);
        return;
      }

      const td = r.querySelector(`td:nth-child(${j + 1})`) as HTMLElement;
      td.style.display = x ? "" : "none";
    });
  });
};
